import React, { useMemo } from "react";
import { PartyPopper } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui-kit";
import { EventShareButtons } from "@/components/ShareButtons";
import { evTitle } from "@/components/EventCard";
import { imgSrc } from "@/lib/img";
import { fmtDateTime } from "@/lib/time";
import { usePrefs } from "@/contexts/PrefsContext";
import type { AppEvent } from "@/types";

const COLORS = ["#FB0330", "#FFC93C", "#1FB5A8", "#5B6CFF", "#FF7A00"];

const Confetti = () => {
  const bits = useMemo(() => Array.from({ length: 36 }, (_, i) => ({
    left: Math.random() * 100, delay: Math.random() * 0.8, dur: 1.8 + Math.random() * 1.4, rot: Math.random() * 360,
    color: COLORS[i % COLORS.length], w: 6 + Math.random() * 6,
  })), []);
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {bits.map((b, i) => (
        <span key={i} className="wn-confetti absolute top-0 rounded-sm" style={{ left: `${b.left}%`, width: b.w, height: b.w * 1.6, background: b.color,
          animationDelay: `${b.delay}s`, animationDuration: `${b.dur}s`, transform: `rotate(${b.rot}deg)` }} />
      ))}
    </div>
  );
};

export const FirstEventCelebration = ({ e, onClose }: { e: AppEvent; onClose: () => void }) => {
  const { t, lang } = usePrefs();
  const title = evTitle(e, lang);
  const date = fmtDateTime(e.start_at, lang, { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
  return (
    <Dialog open onOpenChange={(o: boolean) => !o && onClose()}>
      <DialogContent data-testid="first-event-celebration" className="w-[calc(100%-2rem)] max-w-md rounded-3xl overflow-hidden p-0">
        <div className="relative min-w-0 px-6 pt-10 pb-6 text-center space-y-4">
          <Confetti />
          <div className="mx-auto h-20 w-20 rounded-[1.75rem] bg-primary text-primary-foreground grid place-items-center -rotate-6 shadow-lg wn-pop"><PartyPopper className="h-10 w-10" /></div>
          <DialogTitle className="font-display text-2xl font-black text-center">{t("celebrate.title")}</DialogTitle>
          <p className="text-sm text-muted-foreground">{t("celebrate.sub")}</p>
          <div className="rounded-2xl border bg-background p-3 flex items-center gap-3 text-start" data-testid="celebration-event">
            {e.poster && <img src={imgSrc(e.poster)} alt="" className="h-14 w-14 rounded-xl object-cover" />}
            <div className="min-w-0"><p className="font-bold truncate">{title}</p><p className="text-xs text-muted-foreground">{date}</p></div>
          </div>
          <div className="flex justify-center"><EventShareButtons eventId={e.event_id} title={title} date={date} /></div>
          <button onClick={onClose} data-testid="celebration-close" className="text-sm font-bold text-muted-foreground hover:text-foreground">{t("celebrate.later")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
